<template>
  <div class="welcome-container">
    <!-- 智能体身份区：智能体头像 + 名称 + 功能描述（切换入口在输入区智能体 chip） -->
    <div v-if="quickAgent" class="agent-identity">
      <AgentAvatar
        :avatar="quickAgent.agentAvatar"
        :name="quickAgent.agentName"
        :size="56"
        class="identity-avatar"
      />
      <div class="identity-name">{{ quickAgent.agentName }}</div>
      <div class="identity-desc">{{ agentSummary }}</div>
    </div>

    <!-- 无智能体时的通用欢迎 -->
    <div v-else class="welcome-hero">
      <el-icon :size="48" color="#409eff"><ChatDotRound /></el-icon>
      <h3 class="welcome-title">你好，我是 AI 对话助手</h3>
      <p class="welcome-desc">输入消息开始对话，输入 @ 可指定智能体</p>
    </div>

    <!-- 操作指南：默认仅显示入口，点击入口才在原位内嵌展开问题列表 -->
    <div class="guide-wrap" :class="{ 'is-open': guideOpen }">
      <!-- 入口按钮 -->
      <button class="guide-entry" @click="toggleGuide">
        <el-icon class="guide-entry-icon"><Reading /></el-icon>
        <span class="guide-entry-text">操作指南</span>
        <span class="guide-entry-count">{{ faqTotal }}</span>
        <el-icon class="guide-entry-arrow"><ArrowDown /></el-icon>
      </button>

      <!-- 展开后的问题列表面板 -->
      <div v-show="guideOpen" class="guide-panel">
        <!-- 问题多时提供关键词过滤 -->
        <div v-if="faqTotal > 8" class="guide-panel-search">
          <el-input
            v-model="keyword"
            placeholder="搜索问题关键词"
            clearable
            size="small"
            :prefix-icon="Search"
          />
        </div>
        <div v-loading="faqTreeLoading" class="guide-panel-body">
          <!-- 2026-11-02 主人：面板“就显示了一行，适配很差”。
               el-tree 默认只展开根层，问题全在目录里 => 面板内容只有 1 行高。
               现在恒为展开：点开就是全部问题平铺，装不下靠 max-height 内部滚动 -->
          <el-tree
            v-if="filteredTree.length > 0"
            :key="treeKey"
            :data="filteredTree"
            node-key="faqId"
            :props="{ children: 'children', label: 'faqName' }"
            :default-expand-all="true"
            :expand-on-click-node="true"
            @node-click="onNodeClick"
          >
            <template #default="{ node, data }">
              <!-- 目录节点：名称 + 子项数量 -->
              <div v-if="!isLeaf(data)" class="guide-dir" :title="data.faqName">
                <el-icon class="guide-dir-icon"><Folder /></el-icon>
                <span class="guide-dir-name">{{ node.label }}</span>
                <span class="guide-dir-count">{{ countLeaf(data.children) }}</span>
              </div>
              <!-- 问题节点：问号标记 + 问题关键词 + 提问指引（悬停可见实际发送的提示词） -->
              <div v-else class="guide-question" :title="data.faqContent || data.faqName">
                <span class="guide-question-mark">?</span>
                <span class="guide-question-text">{{ node.label }}</span>
                <span class="guide-question-action">
                  去提问
                  <el-icon><Top /></el-icon>
                </span>
              </div>
            </template>
          </el-tree>
          <div v-else-if="!faqTreeLoading" class="guide-panel-empty">
            {{ keyword.trim() ? '没有匹配的问题' : '暂无操作指南，可在「AI 常见问题」中维护' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ChatDotRound, Reading, Folder, Top, ArrowDown, Search } from '@element-plus/icons-vue'
import AgentAvatar from '@/modules/huyu-ai/backend/components/AgentAvatar/index.vue'

const props = defineProps({
  quickAgent: { type: Object, default: null },
  faqTreeLoading: { type: Boolean, default: false },
  faqTreeData: { type: Array, default: () => [] }
})

const emit = defineEmits(['pick'])

// 指南展开状态：默认收起，点击入口才展开
const guideOpen = ref(false)
const keyword = ref('')

function toggleGuide() {
  guideOpen.value = !guideOpen.value
  if (!guideOpen.value) keyword.value = ''
}

// 浮层展开时点击外部关闭
function handleOutsideClick(e) {
  if (!guideOpen.value) return
  if (e.target.closest('.guide-wrap')) return
  guideOpen.value = false
  keyword.value = ''
}
onMounted(() => document.addEventListener('click', handleOutsideClick))
onUnmounted(() => document.removeEventListener('click', handleOutsideClick))

// 功能描述：与名称重复时不重复展示，改为可提问引导
const agentSummary = computed(() => {
  const a = props.quickAgent
  if (!a) return ''
  const desc = (a.agentDesc || '').trim()
  if (desc && desc !== (a.agentName || '').trim()) return desc
  return '有什么可以帮你的，直接输入问题开始对话'
})

// 无子级即为问题本身，有子级为目录
function isLeaf(data) {
  return !(data && data.children && data.children.length > 0)
}

// 统计问题数量（仅叶子节点）
function countLeaf(nodes) {
  let total = 0
  for (const n of nodes || []) {
    if (isLeaf(n)) {
      if (n.faqName) total += 1
      continue
    }
    total += countLeaf(n.children)
  }
  return total
}

const faqTotal = computed(() => countLeaf(props.faqTreeData))

// 关键词过滤：命中问题保留，目录命中则保留其下全部问题，目录无命中则剔除
// 同时匹配「问题关键词(faqName)」与「提示词(faqContent)」，便于按详情内容搜索
function filterNodes(nodes, kw) {
  const result = []
  for (const n of nodes || []) {
    const hit = (n.faqName || '').toLowerCase().includes(kw)
      || (n.faqContent || '').toLowerCase().includes(kw)
    if (isLeaf(n)) {
      if (hit) result.push(n)
      continue
    }
    if (hit) {
      result.push(n)
      continue
    }
    const children = filterNodes(n.children, kw)
    if (children.length > 0) result.push({ ...n, children })
  }
  return result
}

const filteredTree = computed(() => {
  const kw = keyword.value.trim().toLowerCase()
  if (!kw) return props.faqTreeData
  return filterNodes(props.faqTreeData, kw)
})

// 搜索态下自动展开全部命中项，浏览态保持折叠
const searchMode = computed(() => keyword.value.trim().length > 0)

// el-tree 的 default-expand-all 只在挂载时生效，所以「浏览 <-> 搜索」以及换关键词都要重挂，
// 否则搜索完清空关键词，树还停在搜索态展开/折叠的样子。
const treeKey = computed(() => (searchMode.value ? 'search:' + keyword.value.trim() : 'browse'))

// 点击叶子节点直接发送问题内容；目录节点由 el-tree 自行展开
// 展示的是关键词，实际发送的是提示词（faqContent），为空时回退关键词
function onNodeClick(data) {
  if (!isLeaf(data) || !data.faqName) return
  emit('pick', data.faqContent || data.faqName)
}
</script>

<style scoped>
.welcome-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 24px 16px;
  text-align: center;
}

/* ===== 智能体身份区 ===== */
.agent-identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
}
.identity-avatar {
  box-shadow: 0 4px 16px rgba(31, 35, 40, 0.08);
}
.identity-name {
  margin-top: 12px;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 0.3px;
  color: #1f2328;
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.identity-desc {
  margin-top: 6px;
  max-width: 460px;
  font-size: 13px;
  color: #8b949e;
  line-height: 1.7;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* ===== 通用欢迎（无智能体） ===== */
.welcome-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.welcome-title {
  margin: 12px 0 6px;
  font-size: 19px;
  font-weight: 700;
  color: #1f2328;
}
.welcome-desc {
  margin: 0;
  font-size: 13px;
  color: #8b949e;
}

/* ===== 操作指南：入口 + 展开面板 ===== */
.guide-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 720px;
  margin-top: 22px;
}
.guide-entry {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #ebeef5;
  border-radius: 999px;
  background: #fff;
  font-size: 13px;
  color: #57606a;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.guide-entry:hover {
  border-color: #409eff;
  color: #2b7fd4;
  background: #fafcff;
}
.guide-wrap.is-open .guide-entry {
  border-color: #cfe4ff;
  color: #2b7fd4;
  background: #f2f8ff;
}
.guide-entry-icon {
  font-size: 15px;
}
.guide-entry-text {
  font-weight: 600;
}
.guide-entry-count {
  padding: 1px 7px;
  border-radius: 999px;
  background: #f0f2f5;
  font-size: 11px;
  color: #8b949e;
}
.guide-wrap.is-open .guide-entry-count,
.guide-entry:hover .guide-entry-count {
  background: #e3efff;
  color: #2b7fd4;
}
.guide-entry-arrow {
  font-size: 12px;
  color: #c0c6cc;
  transition: transform 0.18s;
}
.guide-wrap.is-open .guide-entry-arrow {
  color: #409eff;
  transform: rotate(180deg);
}

/* 展开后的列表面板：绝对定位浮层，不占文档流，避免撑动上方智能体介绍 */
/* 展开后的列表面板（2026-11-02 重做）
 * 旧版是 absolute + top:100% —— 它从欢迎区底部往下长，1020x660 的面板里
 * 直接压到输入框上（主人截图：面板被输入区截断，“适配很差”）。
 * 现在改成 fixed：脱离父容器的裁切与文档流，固定在输入区上方向上长，
 * 宽度随视口收（窄屏不留边），高度取「400px 与 视口高-320px」的较小值，
 * 装不下就在 .guide-panel-body 内部滚 —— 任何窗口尺寸都能看全所有问题。 */
.guide-panel {
  position: fixed;
  left: 50%;
  bottom: 172px;
  transform: translateX(-50%);
  z-index: 1200;
  width: min(680px, calc(100vw - 96px));
  max-height: min(400px, calc(100vh - 320px));
  display: flex;
  flex-direction: column;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  text-align: left;
}
.guide-panel-search {
  flex-shrink: 0;
  padding: 10px 12px;
  border-bottom: 1px solid #f2f4f7;
  background: #fafbfc;
}
.guide-panel-body {
  flex: 1;
  min-height: 0;
  padding: 6px 8px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.guide-panel-body::-webkit-scrollbar {
  width: 4px;
}
.guide-panel-body::-webkit-scrollbar-thumb {
  background: #e0e6eb;
  border-radius: 2px;
}
.guide-panel-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90px;
  font-size: 12.5px;
  color: #b6bdc4;
}

/* 树节点容器：让自定义内容铺满整行，便于整行 hover */
.guide-panel-body :deep(.el-tree-node__content) {
  height: auto;
  min-height: 34px;
  padding-right: 4px;
  border-radius: 8px;
}
.guide-panel-body :deep(.el-tree-node__content:hover) {
  background: transparent;
}
.guide-panel-body :deep(.el-tree-node__label) {
  flex: 1;
  min-width: 0;
}
.guide-panel-body :deep(.el-tree-node__expand-icon) {
  color: #c0c6cc;
}

/* ===== 目录节点 ===== */
.guide-dir {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 5px 6px;
  border-radius: 8px;
  transition: background 0.15s;
}
.guide-dir:hover {
  background: #f6f8fa;
}
.guide-dir-icon {
  flex-shrink: 0;
  font-size: 14px;
  color: #f0b429;
}
.guide-dir-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.guide-dir-count {
  flex-shrink: 0;
  padding: 0 6px;
  border-radius: 999px;
  background: #f0f2f5;
  font-size: 11px;
  line-height: 17px;
  color: #8b949e;
}

/* ===== 问题节点 ===== */
.guide-question {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #fafbfc;
  transition: all 0.15s;
}
.guide-question:hover {
  border-color: #cfe4ff;
  background: #f2f8ff;
  box-shadow: 0 1px 4px rgba(64, 158, 255, 0.1);
}
.guide-question-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #e8eef5;
  font-size: 11px;
  font-weight: 700;
  color: #8b949e;
  transition: all 0.15s;
}
.guide-question:hover .guide-question-mark {
  background: #409eff;
  color: #fff;
}
.guide-question-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #57606a;
}
.guide-question:hover .guide-question-text {
  color: #2b7fd4;
}
.guide-question-action {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  font-size: 11.5px;
  color: #409eff;
  opacity: 0;
  transform: translateX(4px);
  transition: all 0.15s;
}
.guide-question:hover .guide-question-action {
  opacity: 1;
  transform: translateX(0);
}

/* ===== 响应式适配 ===== */
@media (max-width: 768px) {
  .welcome-container {
    padding: 16px 10px;
  }
  .identity-name {
    font-size: 17px;
    max-width: 260px;
  }
  .identity-desc {
    font-size: 12.5px;
    max-width: 100%;
  }
  .guide-wrap {
    margin-top: 16px;
  }
  .guide-entry {
    padding: 7px 14px;
    font-size: 12.5px;
  }
  .guide-panel-search {
    padding: 8px 10px;
  }
  .guide-panel {
    bottom: 138px;
    width: calc(100vw - 24px);
    max-height: min(360px, calc(100vh - 240px));
  }
  .guide-panel-body {
    padding: 4px 6px;
  }
  .guide-question {
    padding: 6px;
  }
  .guide-question-action {
    display: none;
  }
}
/* 矮屏（宽屏但高度小）：压缩面板高度 */
@media (max-height: 700px) {
  .welcome-container {
    padding: 14px 16px;
  }
  .guide-wrap {
    margin-top: 14px;
  }
  .guide-panel {
    max-height: min(340px, calc(100vh - 300px));
  }
}
</style>
